import { useEffect, useRef, useState } from 'react';
import { useT } from '@dia/i18n';
import { Button, cn } from '@dia/ui';
import { RequireAuth, type Identity } from './lib/identity';
import { Icon } from './lib/icons';
import { translateText } from './lib/data';
import { whatsappLink } from './lib/pairing';
import type { Lang } from './lib/text';
import { Card, Field, inputClass, Notice, Page } from './lib/ui';

/**
 * Traductor: type or dictate, get the other language, copy it, or send it
 * through WhatsApp. Same translate function and the same cache as everything
 * else, so a phrase asked for twice costs one call.
 */
export function Traductor() {
  return <RequireAuth>{(who) => <TranslatorBody who={who} />}</RequireAuth>;
}

type Recognition = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  stop: () => void;
};

function makeRecognition(): Recognition | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => Recognition;
    webkitSpeechRecognition?: new () => Recognition;
  };
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
}

function TranslatorBody({ who }: { who: Identity }) {
  const { t, lang } = useT();
  const [source, setSource] = useState<Lang>(lang);
  const [text, setText] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [cached, setCached] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [needText, setNeedText] = useState(false);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [noMic, setNoMic] = useState(false);
  const recognition = useRef<Recognition | null>(null);

  useEffect(() => () => recognition.current?.stop(), []);

  const target: Lang = source === 'es' ? 'en' : 'es';

  const go = async () => {
    if (!text.trim()) {
      setNeedText(true);
      return;
    }
    setNeedText(false);
    setBusy(true);
    setProblem(null);
    setResult(null);
    try {
      const r = await translateText(text.trim(), source, target, who.token, who.role === 'owner' ? 'owner' : 'staff');
      if (r.ok) {
        setResult(r.text);
        setCached(r.cached);
      } else {
        setProblem(lang === 'es' ? r.message_es : r.message_en);
      }
    } finally {
      setBusy(false);
    }
  };

  const dictate = () => {
    if (listening) {
      recognition.current?.stop();
      return;
    }
    const rec = makeRecognition();
    if (!rec) {
      setNoMic(true);
      return;
    }
    rec.lang = source === 'es' ? 'es-PA' : 'en-US';
    rec.interimResults = false;
    rec.continuous = false;
    rec.onresult = (event) => {
      const spoken = Array.from(event.results)
        .map((r) => r[0]?.transcript ?? '')
        .join(' ')
        .trim();
      if (spoken) setText((prev) => (prev ? `${prev} ${spoken}` : spoken));
    };
    rec.onend = () => setListening(false);
    rec.onerror = () => {
      setListening(false);
      setNoMic(true);
    };
    recognition.current = rec;
    setListening(true);
    rec.start();
  };

  const copy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Page title={t('ops.translator.title')} back="/mas">
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-2" role="group">
          {(['es', 'en'] as const).map((code) => (
            <button
              key={code}
              type="button"
              aria-pressed={source === code}
              onClick={() => {
                setSource(code);
                setResult(null);
              }}
              className={cn(
                'min-h-tap rounded border px-2 py-2 text-[18px]',
                source === code ? 'border-accent bg-accent font-semibold text-onAccent' : 'border-line bg-surface text-ink',
              )}
            >
              {code === 'es' ? t('ops.translator.from_es') : t('ops.translator.from_en')}
            </button>
          ))}
        </div>

        <Field label={t('ops.translator.input')}>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} className={inputClass} />
        </Field>

        <Button variant="secondary" block onClick={dictate}>
          <Icon name="mic" /> {listening ? t('ops.translator.dictate_stop') : t('ops.translator.dictate')}
        </Button>
        {noMic && <Notice tone="info">{t('ops.translator.dictate_none')}</Notice>}
        {needText && <Notice tone="warn">{t('ops.translator.need_text')}</Notice>}

        <Button size="lg" block busy={busy} busyLabel={t('loading')} onClick={() => void go()}>
          <Icon name="translate" /> {t('ops.translator.go')}
        </Button>

        {problem && <Notice tone="danger">{problem}</Notice>}

        {result && (
          <Card>
            <h2 className="mb-2 text-[1.15em] font-semibold">{t('ops.translator.result')}</h2>
            <p className="m-0 text-[1.1em]">{result}</p>
            {cached && <p className="mt-1 text-inkMuted">{t('ops.translator.cached')}</p>}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button variant="secondary" onClick={() => void copy()}>
                {copied ? t('ops.translator.copied') : t('ops.translator.copy')}
              </Button>
              <a
                href={whatsappLink(result)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-tap items-center justify-center rounded border border-line bg-surface px-3 text-ink no-underline"
              >
                {t('action.open_whatsapp')}
              </a>
            </div>
          </Card>
        )}
      </div>
    </Page>
  );
}
